<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import {
  ClassicalFireResultCard,
  DwzResultCard,
  formatCzk,
  formatCzkShort,
  splitCzkShortSuffix,
  stripCzkShortUnit,
} from '@fire-calc/ui-vue';
import { CalculationMode } from '@fire-calc/domain-ts';
import { useCalculatorStore } from '../state/calculatorStore';

const store = useCalculatorStore();
const { planResult, scenario, mode } = storeToRefs(store);
const { t } = useI18n();

function formatPmt(value: number): string {
  return stripCzkShortUnit(formatCzkShort(value));
}

const heroLabel = computed(() =>
  mode.value === CalculationMode.byRetirementAge
    ? t('heroLabelRequiredInvestment', { age: scenario.value.retirementAge.value })
    : t('heroLabelSolvedRetirementAge'),
);

const heroUnit = computed(() =>
  mode.value === CalculationMode.byRetirementAge
    ? t('heroUnitPerMonth')
    : t('heroUnitYears'),
);

const classicalHero = computed(() => {
  if (!planResult.value?.classicalFire) return { number: '—', suffix: '' };
  if (mode.value !== CalculationMode.byRetirementAge) {
    return {
      number: planResult.value.classicalFire.solvedRetirementAge?.value?.toString() ?? '—',
      suffix: '',
    };
  }
  return splitCzkShortSuffix(
    formatPmt(planResult.value.classicalFire.requiredMonthlyInvestment.value),
  );
});

const dwzHero = computed(() => {
  if (!planResult.value?.dieWithZero) return { number: '—', suffix: '' };
  if (mode.value !== CalculationMode.byRetirementAge) {
    return {
      number: planResult.value.dieWithZero.solvedRetirementAge?.value?.toString() ?? '—',
      suffix: '',
    };
  }
  return splitCzkShortSuffix(
    formatPmt(planResult.value.dieWithZero.requiredMonthlyInvestment.value),
  );
});

const classicalFireNumber = computed(
  () => planResult.value?.classicalFire
    ? formatCzkShort(planResult.value.classicalFire.fireNumber.value)
    : '—',
);
const dwzFireNumber = computed(
  () => planResult.value?.dieWithZero
    ? formatCzkShort(planResult.value.dieWithZero.fireNumber.value)
    : '—',
);

const bridgeText = computed(() => t('bridgeSubtext'));

const classicalSubheader = computed(() =>
  t('resultClassicalDesc', {
    swr: scenario.value.swr.asPercent.toFixed(1).replace('.', ','),
  }),
);

const classicalFireNumberTooltip = computed(() => {
  const future = planResult.value?.classicalFire;
  if (!future) return null;
  // `formatCzk` returns "44 778 400 Kč"; the i18n template already appends " Kč",
  // so strip the helper's suffix before interpolating.
  const valueOnly = formatCzk(future.futureNominalValue).replace(/ Kč$/, '');
  return t('resultFutureNominalTooltip', { value: valueOnly });
});

const dwzFireNumberTooltip = computed(() => {
  const future = planResult.value?.dieWithZero;
  if (!future) return null;
  // Same `formatCzk` suffix-strip rule as `classicalFireNumberTooltip`.
  const valueOnly = formatCzk(future.futureNominalValue).replace(/ Kč$/, '');
  return t('resultFutureNominalTooltip', { value: valueOnly });
});
</script>

<template>
  <div data-testid="panel-results" class="flex flex-col gap-xl">
    <h2 class="text-headline-medium text-text-primary">{{ t('resultTitle') }}</h2>
    <ClassicalFireResultCard
      :mode="mode"
      :subheader="classicalSubheader"
      :hero-label="heroLabel"
      :hero-unit="heroUnit"
      :hero-value="classicalHero.number"
      :hero-suffix="classicalHero.suffix"
      :bridge-text="bridgeText"
      :fire-number="classicalFireNumber"
      :fire-number-tooltip="classicalFireNumberTooltip"
    />
    <DwzResultCard
      :mode="mode"
      :hero-label="heroLabel"
      :hero-unit="heroUnit"
      :hero-value="dwzHero.number"
      :hero-suffix="dwzHero.suffix"
      :bridge-text="bridgeText"
      :fire-number="dwzFireNumber"
      :fire-number-tooltip="dwzFireNumberTooltip"
    />
  </div>
</template>
